/**
 * キャンセルの読み取り(2026-09-28 発注者提供のCSVで確認。P-5 / P-67②)。
 *
 * BlueBean の**顧客情報レポート**(次アクションで絞らずに出したもの)には、
 * キャンセルになったアポが **`対応者情報` = 「キャンセル」** として残っている。
 *  - **`リアクション情報(最新)` は「アポ〇」のまま**なので、**取得件数は減らない**(過去の集計が変わらない)
 *  - `最終保存オペレーター(最新)`(例 `RS4006 (柴﨑　陽子)`)・`登録者名` から **誰のアポか**が分かる
 *  - 日付は3つ取れる: `新規日時(最新)`=取得した日 ／ `アクション予定時間`=訪問予定日 ／ `更新日時(最新)`=キャンセルが分かった日
 *
 * **顧客の氏名・電話番号・住所は読み捨てる**(件数だけを返す。CLAUDE.md セキュリティ)。
 */
import type { DateStr } from '@core/lib/types';

export const CANCEL_COLUMNS = {
  contact: '対応者情報',
  reaction: 'リアクション情報(最新)',
  created: '新規日時(最新)',
  updated: '更新日時(最新)',
  planned: 'アクション予定時間',
  operator: '最終保存オペレーター(最新)',
  operator_name: '登録者名',
  business: '業務コード(最新)',
} as const;

/** 対応者情報がこの値ならキャンセル */
export const CANCEL_MARK = 'キャンセル';

/** どの日付でその月のキャンセルと数えるか */
export type CancelBasis = 'created' | 'planned' | 'updated';
export const CANCEL_BASIS_LABELS: Record<CancelBasis, string> = {
  created: '取得した日',
  planned: '訪問予定日',
  updated: 'キャンセルが分かった日',
};

export type CancelResult = {
  /** BB ID → 件数 */
  by_bb_id: Record<string, number>;
  /** BB ID が読めなかった行(名前だけの行など) */
  unknown: { name: string; count: number }[];
  total: number;
  /** 見つかった月(重複なし) */
  months: string[];
  /** CSV に「対応者情報」の列が無い */
  no_column: boolean;
  /** その月以外だったので数えなかった件数 */
  other_month: number;
  /** 業務コード(会社)。1ファイル1業務の想定 */
  businesses: string[];
};

/** "2026-09-10 14:52:41" → "2026-09-10"。読めなければ null */
export function dateOf(s: string | undefined): DateStr | null {
  const m = (s ?? '').trim().match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  return m ? (`${m[1]}-${m[2]!.padStart(2, '0')}-${m[3]!.padStart(2, '0')}` as DateStr) : null;
}

/** "RS4006 (柴﨑　陽子)" → "RS4006"。BB ID が読めなければ null */
export function bbIdOf(s: string | undefined): string | null {
  const m = (s ?? '').trim().match(/^([A-Za-z]{1,4}\d{2,6}[A-Za-z]?)/);
  return m ? m[1]!.toUpperCase() : null;
}

/**
 * 顧客情報レポートの行から、その月のキャンセル件数を BB ID ごとに数える。
 * `month` を渡すとその月(YYYY-MM)だけ。`basis` はどの日付で月を決めるか
 */
export function countCancels(rows: Record<string, string>[], month: string, basis: CancelBasis = 'created'): CancelResult {
  const byBbId: Record<string, number> = {};
  const unknown = new Map<string, number>();
  const months = new Set<string>();
  const businesses = new Set<string>();
  let total = 0;
  let otherMonth = 0;
  const noColumn = rows.length > 0 && !(CANCEL_COLUMNS.contact in rows[0]!);

  const dateCol = basis === 'planned' ? CANCEL_COLUMNS.planned : basis === 'updated' ? CANCEL_COLUMNS.updated : CANCEL_COLUMNS.created;
  for (const r of rows) {
    if ((r[CANCEL_COLUMNS.contact] ?? '').trim() !== CANCEL_MARK) continue;
    const date = dateOf(r[dateCol]);
    if (date) months.add(date.slice(0, 7));
    if (month && date && date.slice(0, 7) !== month) {
      otherMonth++;
      continue;
    }
    total++;
    const biz = (r[CANCEL_COLUMNS.business] ?? '').trim();
    if (biz) businesses.add(biz);
    const bb = bbIdOf(r[CANCEL_COLUMNS.operator]);
    if (bb) {
      byBbId[bb] = (byBbId[bb] ?? 0) + 1;
    } else {
      const name = (r[CANCEL_COLUMNS.operator_name] ?? '').trim() || '(名前なし)';
      unknown.set(name, (unknown.get(name) ?? 0) + 1);
    }
  }
  return {
    by_bb_id: byBbId,
    unknown: [...unknown.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count),
    total,
    months: [...months].sort(),
    no_column: noColumn,
    other_month: otherMonth,
    businesses: [...businesses],
  };
}
